import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import '../css/Pos.css';

/**
 * Which staff a sales report covers.
 *
 * The list is whoever actually took money in the loaded range, so it lives
 * with the RESULTS rather than with the date inputs above them: it cannot be
 * built until the orders are in hand, and a filter rendered empty next to the
 * button that would populate it reads as broken.
 *
 * TRACKS WHO IS UN-TICKED. Everyone starts included, so a report answers the
 * usual question without anyone touching this, and a name that first appears
 * in a newly-fetched range arrives included rather than silently filtered out.
 * See filterPosOrdersByCashier.
 *
 * @param {object} props
 * @param {Array<{key: string, label: string, orderCount: number, total: number}>} props.staff
 *   - from listPosCashiers, biggest takings first
 * @param {Set<string>} props.excluded - cashier keys currently un-ticked
 * @param {(excluded: Set<string>) => void} props.onChange
 * @returns {JSX.Element|null} nothing when one person took every order, where
 *   a filter offers no choice
 */
function PosStaffFilter({ staff, excluded, onChange }) {
  const { config } = useConfig();
  if (!staff || staff.length < 2) return null;

  const includedCount = staff.filter((row) => !excluded.has(row.key)).length;
  const allOn = includedCount === staff.length;

  /**
   * Ticks or un-ticks one person.
   * @param {string} key
   * @returns {void}
   */
  const toggle = (key) => {
    const next = new Set(excluded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  return (
    <div className="pos-stafffilter">
      <div className="pos-stafffilter-head">
        <span className="pos-stafffilter-label">Staff</span>
        {/* One control that reads as what it will DO. "Select all" beside an
            already-full list would be a button that does nothing. */}
        <button
          type="button"
          className="pos-stafffilter-toggle"
          onClick={() => onChange(allOn ? new Set(staff.map((row) => row.key)) : new Set())}
        >
          {allOn ? 'Clear all' : 'Select all'}
        </button>
      </div>

      <div className="pos-stafffilter-list">
        {staff.map((row) => {
          const on = !excluded.has(row.key);
          return (
            <label key={row.key} className={`pos-staffchip${on ? ' on' : ''}`}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(row.key)}
              />
              <span className="pos-staffchip-name">{row.label}</span>
              {/* What each person contributed, so the filter doubles as the
                  quickest read of who took what — without it, deciding who to
                  un-tick means ticking them one at a time to find out. */}
              <span className="pos-staffchip-total">{formatMoney(row.total, config)}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default PosStaffFilter;
